/*
 * Copyright (c) 2025. Jellybean B.V.
 * Jellybean B.V. Apeldoorn heeft een licentie verleend aan
 * Spin in het Web B.V. Apeldoorn voor het gebruik van deze software-
 * onderdelen in projecten voor derden. U heeft géén toestemming
 * tot inzage of wijziging in de (bron)code van deze onderdelen.
 */

/*
* datatable.component.ts
* 10-12-2019 - Jelmer Jellema - Spin in het Web B.V.
*
* De datatablecomponent
*/

//TODO: template-handlers (oude $scope.functies)
//TODO: contextmenu
//TODO: datumpicker, multipicker, vantotpicker


import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import {cloneDeep, isEqual, isObject} from 'lodash-es';

import moment from 'moment';
import {Sihwlog, Sihwlogger} from '../sihwlog/sihwlog';
import {localConfig} from '../localconfig/localconfig';
import {KeyedTimeout} from '../keyedtimeout/keyedtimeout';
import {DomSanitizer} from '@angular/platform-browser';

//helpers
interface hash<Type> {
  [key: string]: Type;
}

type rijfunctie<Type> = (val: any, rij?: any) => Type;

interface DatatableVelddef {
  hide?: boolean, //skip dit veld?
  label?: string | false, //header - default de veldkey
  prop?: string | rijfunctie<any>, ///de property in het record Kan met punten.voor.diepte. Standaard de key. Kan ook een functie zijn, die dan de val teruggeef
  type?: string | [string, hash<any>], //typedefinitie
  format?: rijfunctie<string>,
  html?: boolean,
  style?: rijfunctie<string> | hash<any>, //ngStyle-achtig? TODO: aanpassen
  sortval?: rijfunctie<string>,
  filterval?: rijfunctie<string>,
  filter?: boolean, //toon filter (als er een filterbalk is)
  groep?: string, //label van de groep waarin dit veld valt
  standaardfilter?: string | number | hash<any>
}

//definieert hoe er geselecteerd kan worden
interface DatatableSelecteerdef {
  label?: string, //label boven selectiehokje-kolom
  setProp?: string, //prop die bij nieuwe rijen tot selectie leidt
  selecteerbaar?: (rij: hash<any>) => boolean, //callback die bepaalt of een rij selecteerbaar is
  filterSelect?: boolean //als true dan zijn rijen die niet in het filter zitten niet te seleteren
}

//definieert hoe er gekozen kan worden
interface DatatableKiesdef {
  label?: string, //label boven kies-kolom(men)
  positie?: 'links' | 'rechts' | 'beide',
  content?: string | { links: string, rechts: string }, //af te beelden content (standaard een icon)
  class?: string, //classes rond de a href voor kiezen. Standaard btn btn-primary
  kiesbaar?: (rij: hash<any>) => boolean, //bij true is de rij kiesbaar
  kies?: (rij: hash<any>) => void //callback bij kiezen (TODO: via notify)
}

export interface DatatableSortering {
  key: string,
  keytype?: 'veld' | 'prop', //standaard veld
  richting?: number //standaard 1
}

export interface DatatableDef {
  track?: string, //welk veld wordt getracked in de repeat
  lognaam?: string, //naam van deze datatable tbv het loggen
  selecteer?: DatatableSelecteerdef, //de selecteerdef,
  kies?: DatatableKiesdef, //kiesdef, mogelijkheid tot kiezen van een rij
  standaardsortering?: DatatableSortering,
  filter?: boolean,
  filterOpVerzoek?: boolean,
  rijtitle?: rijfunctie<string>,
  rijclasses?: rijfunctie<string[]>, //geeft gegeven de rij een aantal classes terug
  onFilterChanged?: (wijzigingen: hash<any>) => void,
  velden: hash<DatatableVelddef>
}

interface DatatableRijMeta {
  data: hash<any>, //rijdata
  infilter: boolean,
  geselecteerd: boolean,
  origvolgorde: number,
  rijclasses: string[],
  rijtitle: string,
  selecteerbaar: boolean,
  kiesbaar: boolean,
  stripe: boolean, //moet deze rij gestriped worden?
  //caches, zie getCached
  _val: hash<any>, //per veld
  style: hash<any>,
  format: hash<any>,
  format_html: hash<any>,
  sortval: hash<any>,
  filterval: hash<any>,
  classes: hash<any>
}

@Component({
  selector: 'datatable',
  templateUrl: './datatable.component.html',
  styleUrls: ['./datatable.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SihwDatatable {
  private log: Sihwlogger;
  private readonly opties: hash<any>;

  @Input('autoUpdate') autoUpdate: boolean = true;
  // altijd op wijzigingen? Anders alleen in setter en bij update()
  // @Input('data') _data: any[] = []; //setter
  private _data: hash<any>[] = [];
  private _oldData: hash<any>[] = []; //ter vergelijking
  // @Input('definition') _def: DatatableDef = null; //setter
  private _def: DatatableDef | null = null;
  private _oldDef: DatatableDef | null = null;
  @Output('rijklik') rijklik = new EventEmitter<hash<any>>();

  //interne uitwerking voor in de ux. Definitie en defaults
  public t = {
    velden: <hash<any>[]> [],
    toonfilter: <boolean> true, //toon het filter?
    filterOpVerzoek: <boolean> false, //filteren wanneer dat via de publieke call moet?
    filter: <hash<any>> {}, //waar wordt op gefilterd?
    sorteerveld: <string | null> null, //veld waarop gesorteerd wordt
    tracker: <string | null> null,
    trackRij: (i: number, rij: any) => rij, //de rijtracker. Standaard op objectidentity
    groepen: <(null | hash<any>)[] | null> [],
    selecteer: <hash<any> | null> null,
    allesGeselecteerd: <boolean> false,
    numgeselecteerd: <number> 0,
    kies: <hash<any> | null> null,
    data: <DatatableRijMeta[]> [], //de uiteindelijk af te beelden data
    numfilter: 0 //hoeveel in filter?
  };

  //private werkdingen
  private w = {
    datageneratie: 0, //voor asynchroon toepassen
    loading: false, //zijn we aan het vullen?
    sorteerveld: <hash<any> | null> null, //veld waarop gesorteerd wordt, uit velden
    velden: <hash<hash<any>>> {}, //parsen velden op key
    lognaam: '-', //uit de def
    vorigefilter: <hash<any>> {}, //voor als we zelf filteren uitstellen
    filterchain: <any[]> [], //gegeneerde filterfuncties
    data: <DatatableRijMeta[]> [], //de binnenkomende data, maar in een mantel met data:<data> en metagegevens
    datalength: <number> 0 //rekenen we 1 keer uit
  };

  private keyedTimeout = new KeyedTimeout(); //handelt de timeouts

  //intern
  @ViewChild('table', {static: true}) table?: ElementRef;
  @ViewChild('container', {static: true}) tableContainer?: ElementRef;
  headersTransform: number = 0; //voor sticky headers, zie containerScroll

  constructor(public sanitizer: DomSanitizer, private cdf: ChangeDetectorRef, conf: localConfig, sihwlog: Sihwlog) {
    this.opties = conf.$('SIHW.datatable', {});
    this.log = sihwlog.logger(this.opties.logLevel || 'warn');
    this.log.debug(`Datatable`);

  }

  //setters
  @Input('data')
  public set data(d: any[]) {
    this.log.debug(`Datatable: datasetter`);
    this._data = d; //shallowcopy, zodat we op wijzigingen binnen de array kunnen checken
    this.doeCheck(); //en check nu
  }

  @Input('definition')
  public set definition(def: DatatableDef) {
    this.log.debug(`Datatable: definitionsetter`);
    this._def = def; //shallowcopy, zodat we op wijzigingen binnen het object kunnen checken
    this.doeCheck(); //en check nu
  }

  ///////////////////////////// PUBLIEK ////////////////////////////
  /**
   * Check of we definitie of data moeten bijwerken. Alleen nodig als autoUpdate op false staat
   */
  public update(): void {
    this.log.debug(`Datatable: update`);
    this.doeCheck(true);
  }

  /**
   * Aantal rijen totaal
   */
  public get rijen(): number {
    return this.w.datalength;
  }

  /**
   * Aantal rijen zichtbaar
   */
  public get filterRijen(): number {
    return this.t.numfilter;
  }

  /**
   * Geef aantal geselecteerde rijen terug
   */
  public get selectieRijen(): number {
    return this.t.numgeselecteerd;
  }

  /**
   * Geef de geselecteerde rijen terug
   */
  public get geselecteerd(): hash<any>[] {
    return (this.w.data || []).filter(meta => meta.infilter && meta.geselecteerd).map(meta => meta.data);
  }

  /**
   * Geef data van de gefilterde rijen terug, in de volgorde van zichtbare velden
   * @param koppen Als true, dan is de eerste rij een rij met veldnamen (in hash of array)
   */
  public exportdata(koppen: boolean = false): any[][] {
    let velden = this.t.velden.filter((v :hash<any>) => !(v.exporthide || v.type === 'actie'));

    let res: string[][] = [];
    if (koppen) {
      res.push(velden.map((v: hash<any>) => v.label?.length ? v.label : v.key));
    }
    this.log.debug(this.w.data);
    res.push(...(this.w.data || []).filter(meta => meta.infilter).map(meta => {
          return velden.map(v => {
            let val: any;
            if (v.exportformat) {
              //dan gebruiken we die
              try {
                val = v.exportformat(meta._val[v.key], meta.data);
              } catch (e) {
                this.log.error(e);
                val = "#error";
              }
            } else {
              switch (v.type) {
                  //specials...
                case 'bool':
                  val = meta._val[v.key] ? 1 : 0
                  break;
                default:
                  val = meta.format[v.key];
              }
            }
            return val;
          });
        })
    );
    return res;
  }

  ///////////////////////////// ux events //////////////////////////////
  /**
   * Klik op header: Pas de sortering aan
   * @param veld
   */
  doesorteer(veld: hash<any>): void {
    this.log.debug(`desorteert ${veld.key}`);
    //werd dit veld al gesorteerd? Klikken rouleert  asc, desc, niets
    if (veld.sortering === 1) {
      //nu de andere kant op
      veld.sortering = -1;
    } else if (veld.sortering === -1) {
      //nu niets meer
      veld.sortering = 0;
      this.w.sorteerveld = null;
    } else {
      //oude sorteerveld telt niet meer. Nu wordt het deze
      if (this.w.sorteerveld) {
        this.w.sorteerveld.sortering = 0;
      }
      this.w.sorteerveld = veld;
      veld.sortering = 1;
    }
    this.sorteerData();
    //en toepassen
    this.toepassenData();
  }

  /**
   * rouleer een boolfilter van 0 naar 1 (ja) naar 2 (nee) naar 0 (onbepaald)
   * @param veld
   */
  boolfilter_switch(veld: hash<any>): void {
    this.t.filter[veld.key] = [1, 2, 0][this.t.filter[veld.key] || 0];
    this.filterInputChange(false); //mag direct
  }

  /**
   * Geklikt op een rij. Emit dat met rijklik
   * @param rij
   */
  doerijklik(rij: DatatableRijMeta): void {
    this.log.debug(`rijklik`, rij.data);
    //TODO: opvangen selecteren
    this.rijklik.emit(rij.data);
  }


  /**
   * Klik op selecteeralles. Afhankelijk van de huidige situatie selecteren of deselecteren we alles in het huidige filter. Dit is los van filterselect
   */
  selecteerAlles() {
    for (let rij of this.w.data.filter(rij => rij.infilter && rij.selecteerbaar)) {
      rij.geselecteerd = !this.t.allesGeselecteerd;
    }
    this.bepaalSelectie();
  }

  /**
   * Selecteer de genoemde rijen. Dit moeten object-identity dezelfde zijn of dezelfde tracking hebben. En ze moeten zichtbaar zijn in het filter. Alle andere rijen in het filter worden deselected
   * @param rijen
   */
  selecteerRijen(rijen: hash<any>[]) {
    if (!this.t.selecteer) {
      return; //niets te doen
    }
    for (let rij of this.w.data.filter(rij => rij.infilter && rij.selecteerbaar)) {
      rij.geselecteerd = !! (rijen.includes(rij.data) || (this.t.tracker  && rijen.some(rijenrij => this.t.tracker && rijenrij[this.t.tracker] === rij.data[this.t.tracker])));
    }
    this.bepaalSelectie();
  }

  /**
   * Selecteer alle rijen met de opgegeven css class als rijclass. Deselecteer alle andere. Gaat alleen om de zichtbare rijen
   * @param klass
   */
  selecteerRijclass(klass: string) {
    if (!this.t.selecteer) {
      return; //niets te doen
    }
    for (let rij of this.w.data.filter(rij => rij.infilter && rij.selecteerbaar)) {
      rij.geselecteerd = rij.rijclasses.includes(klass);
    }
    this.bepaalSelectie();
  }

  /**
   * Klik op checkbox of cel voor toggle selectie
   * @param rij
   * @param wissel True als op de checkbox wordt geklikt, anders false: dan is het alleen maar opvangen
   * @param event
   */
  selectClick(rij: DatatableRijMeta, wissel: boolean, event?: MouseEvent) {
    this.log.debug(`selectClick`, rij, wissel);
    if (wissel && this.t.selecteer && rij.selecteerbaar) {
      rij.geselecteerd = !rij.geselecteerd;
    }
    this.bepaalSelectie();
    if (event) {
      event.stopPropagation(); //geen rijklik
    }
  }

  //placeholders
  kiesClick(rij: hash<any>) {
    //todo
  }


  //////////////////////////// events //////////////////////////////////

  /**
   * Regel de stickeyheader. Event komt alleen als container gepositioneerd is (en dat doen we alleen als stickyheader attribute er is) en de host een hoogte heeft (te regelen door parentcomponent).
   * @param e
   */
  containerScroll(e: any) {
    this.log.debug(`datatable: containerscroll`);
    if (this.tableContainer) {
      this.headersTransform = this.tableContainer.nativeElement.scrollTop; //ngstyle doet de rest
      this.log.debug(`headersTransform: ${this.headersTransform}`);
    }
  }

  /**
   * Een filterveld is gewijzigd. Wij doen onze interne filterChanged na een debounce
   */
  filterInputChange(debounce: boolean = true): void {
    //en een nieuwe setten, ook als debounce false is. Dan gewoon direct async
    this.log.debug(`Datatable: filterInputChange`, debounce);
    this.keyedTimeout.set('datatable.debounce', () => {
      this.filterChanged();
    }, debounce ? 400 : 0);
  }

  /**
   * Check op wijzigingen in def en data. Werk bij als dat er is. Geef true als er wijzigingen zijn
   * force = true: forceer wijzigingen (vanuit update)
   */
  private doeCheck(force: boolean = false): boolean {
    //check of de definitie gewijzigd is.
    //dat doen we met olddef en lodash isEqual

    this.log.debug(`Datatable: doeCheck`);
    let gewijzigd: boolean = false;
    if (force || !isEqual(this._def, this._oldDef)) {
      //def changed
      this.log.debug(`doeCheck: force / defChanged`);
      this._oldDef = cloneDeep(this._def); //bewaren
      this.defChanged();
      this.cdf.markForCheck();
      gewijzigd = true;
    }

    //indata idem, maar altijd opnieuw als de def gewijzigd is (voor als hij eerder komt)
    if (gewijzigd || force || !isEqual(this._data, this._oldData)) {
      this.log.debug(`doeCheck: force / defChange / data changed`);
      this._oldData = cloneDeep(this._data);
      this.indataChanged();
      this.cdf.markForCheck();
      gewijzigd = true;
    }

    return gewijzigd;
  }


  /**
   * Aangeroepen door ngDoCheck als de definitie is gewijzigd. Dus ook bij init
   */
  private defChanged() {
    this.log.debug(`Datatable: Defchanged`);
    const $this = this;
    //we gaan onze tabel hernieuwen

    //reset de tabeldef
    let t = (this.t = {
      velden: <hash<any>[]> [],
      toonfilter: <boolean> true, //toon het filter?
      filterOpVerzoek: <boolean> false, //filteren wanneer dat via de publieke call moet?
      filter: <hash<any>> {}, //waar wordt op gefilterd?
      sorteerveld: <string | null> null, //veld waarop gesorteerd wordt
      tracker: <string | null> null,
      trackRij: (i: number, rij: any) => rij, //de rijtracker, standaard op object-identity
      groepen: <(null | hash<any>)[] | null> [], //null-groep betekent "geen groep"
      selecteer: <hash<any> | null> null,
      allesGeselecteerd: <boolean> false,
      numgeselecteerd: <number> 0,
      kies: <hash<any> | null> null,
      data: <DatatableRijMeta[]> [], //de uiteindelijk af te beelden data, na filteren
      numfilter: 0
    });

    let d = this._def;

    if (!d) {
      return;
    }
    let w = this.w; //snelheid

    //bewaar de oude sortering
    let oudeSorteerveld = w.sorteerveld;

    //reset ook het een en ander in de werkdata
    w.velden = {};
    w.sorteerveld = null;
    w.filterchain = [];

    //overnemen:
    t.tracker = d.track || null;
    if (t.tracker) {
      t.trackRij = (i, rij) => {
        // this.log.debug(`trackrij ${i}= rij.data[${t.tracker}] ${rij.data[<string> t.tracker]}`, rij);
        return rij.data[<string> t.tracker]; //dit dus doen
      };
    }
    w.lognaam = d.lognaam || '-';
    t.toonfilter = (!('filter' in d)) || (!!d.filter); //default true
    t.filterOpVerzoek = !!d.filterOpVerzoek;

    //de standaard callbacks
    const cb = this._standaardCallbacks();

    //de opties hierheen halen, zodat de overige callback erbij kunnen
    const opties = this.opties;


    ////////////////////////////////////////////////////////////////////

    //loop over de velden en houd wat dingen bij
    let huidige_groep: any = null;
    let heeftgroepen = false;
    t.groepen = [];

    for (let veld of Object.keys(d.velden)) {
      let velddef: DatatableVelddef = d.velden[veld];
      //hidden veld gewoon opbouwen, en alleen in ux en groepen skippen. Dan loopt de data goed door.

      if (velddef.groep) {
        if (!velddef.hide) {
          heeftgroepen = true;
          if (huidige_groep && huidige_groep.label === velddef.groep) {
            huidige_groep.cols++;
            w.velden[huidige_groep.velden[huidige_groep.velden.length - 1]].groep_laatste = false; //want dat is nu deze
            huidige_groep.velden.push(veld);
          } else {
            huidige_groep = {
              label: velddef.groep,
              velden: [veld],
              cols: 1
            };
            t.groepen.push(huidige_groep); //deze groep bij deze kolom
          }
        }
      } else {
        huidige_groep = false;
        if (!velddef.hide) {
          t.groepen.push(null); //een geen-groep bij deze kolom
        }
      }

      //voeg het filter toe. Als kopie, want dit gaat de ux in
      t.filter[veld] = velddef.standaardfilter ? cloneDeep(velddef.standaardfilter) : null;
      //goed, wat gaan we intern opslaan over dit veld?
      let parsed: hash<any> = {
        key: veld,
        hide: velddef.hide, //
        groep: huidige_groep, //meteen alle info
        groep_eerste: huidige_groep && huidige_groep.cols === 1,
        groep_laatste: !!huidige_groep, //vooralsnog wel, wordt hierboven gecorrigeerd
        label: ('label' in velddef) ? velddef.label : veld,
        filter: ('filter' in velddef) ? velddef.filter : true,
        propfn: <rijfunctie<any> | null> (typeof (velddef.prop) === 'function' ? velddef.prop : null),
        prop: <string[]> (typeof (velddef.prop) === 'function' ? [] : (velddef.prop || veld).split('.')), //array, bijv [project,klant,naam]
        sortering: 0, //1 = sorteer asc, -1 = desc
        filtertype: 'vrij', //filter gebruikt standaard vrije invoer - zie switch hieronder bij bepalen type
        //defaults:
        //_val, de functie die de basiswaarde bepaalt, wordt hieronder gezet
        format: cb.standaardformat, //functie voor teruggeven geformatteerde waarde gegeven een rij  (standaard: de waarde)
        style: cb.standaardstyle, //functie voor het teruggeven van ngStyle-entrie gegeven een rij (standaard: niets)
        sortval: cb.standaardsortval, //functie voor teruggeven waarde tbv sorteren gegeven een rij (standaard: hetzelfde als format)
        filterval: cb.standaardfilterval,
        classes: null //functie voor teruggeven een class of array van classes gegeven een rij (standaard: niets)
      };

      //is dit de oude sortering?
      if (oudeSorteerveld && oudeSorteerveld.key === veld) {
        //dit is dezelfde, maar een nieuw object. Overnemen
        parsed.sortering = oudeSorteerveld.sortering;
        w.sorteerveld = parsed; //deze dus
      }

      //functie voor ophalen waarde:
      parsed._val = function(rij: hash<any>) {
        let cur: hash<any> | null = rij.data;
        let prop: string;
        if (this.propfn)
        {
          //er is een propfunctie, dan nemen we die dus
          return cur ? this.propfn(cur) : null;
        }
        for (let i = 0, _l = this.prop.length; i < _l; i++) {
          prop = this.prop[i];
          if (cur !== null && isObject(cur) && (prop in cur)) {
            cur = (<hash<any>> cur)[prop];
          } else {
            cur = null;
            break;
          }
        }
        return cur;
      };


      //het type
      if (!('type' in velddef)) {
        parsed.type = 'string';
        parsed.typeArgs = {};
      } else {
        if (Array.isArray(velddef.type)) {
          //dan is [0] het type en [1] een propsobject (of undefined)
          parsed.type = velddef.type[0]; //dat is er maar eentje
          parsed.typeArgs = velddef.type[1] || {};
        } else {
          parsed.type = velddef.type;
          parsed.typeArgs = {};
        }
      }

      ////////////////////////// REGEL HET PER TYPE ///////////////////////
      switch (parsed.type) {
        case 'string':
          //alles standaard, behalve format, als er een max-length is
          if (parsed.typeArgs.maxlength) {
            if (velddef.html) {
              this.log.warn(`Datatable ${w.lognaam}: veld ${veld} heeft maxlength én html. Dat gaat niet samen.`);
            } else {
              parsed.format = function(rij: hash<any>) {
                let res = rij._val[this.key];
                if (res && res.length > this.typeArgs.maxlength) {
                  res = res.substr(0, this.typeArgs.maxlength) + '...';
                }
                return res;
              };
            }
          }
          break;
        case 'moment':
          //een momentjs-moment of iets dat daarin parst
          //args kan format bevatten, anders gewoon momentjs format
          //en ook filtertype 'datum' voor de datumpicker
          parsed.html = false; //tenzij dat zo bij custom weer true wordt
          parsed.format = function(rij: DatatableRijMeta) {
            let m = moment(rij._val[this.key]);
            return (m.isValid()) ? m.format(this.typeArgs.format) : '';  //kan undef zijn
          };
          parsed.sortval = function(rij: DatatableRijMeta) {
            let v = rij._val[this.key];
            if (!v) {
              return '';
            }

            return moment(v).valueOf();
          };
          //filteren
          if (parsed.typeArgs.filtertype === 'datum') {
            parsed.filtertype = 'datum';
            parsed.filterval = function(rij: DatatableRijMeta) {
              return rij._val[this.key] ? moment(rij._val[this.key]).format('YYYY-MM-DD') : null;
            };
          } else {
            //filteren op geformateerde versie
            parsed.filterval = function(rij: DatatableRijMeta) {
              return rij.format[this.key];
            };

            parsed.filtertype = 'vrij'; //gewoon vrij
          }
          break;
        case 'bool':
          //boolean: geen veldwaarde, alleen classes
          parsed.format = null;
          parsed.html = false; //tenzij dat zo bij custom weer true wordt
          parsed.classes = function(rij: DatatableRijMeta) {
            if (this.format) {
              return null; //dan eigen inhoud
            }
            if (rij._val[this.key]) {
              return ['datatable_bool_ja fas fa-check']; //zie readme: we doen fa voor als hij er is
            } else if (rij._val[this.key] === null) {
              return ['datatable_bool_onbepaald fas fa-ellipsis-h'];
            } else {
              return ['datatable_bool_nee fas fa-times'];
            }

          };
          parsed.filtertype = 'bool'; //boolfilter
          parsed.sortval = function(rij: DatatableRijMeta) {
            return rij._val[this.key] ? 1 : -1;
          };
          break;
        case 'enum':
          parsed.html = false; //gewoon niet
          parsed.filtertype = 'enum'; //apart filter
          //verder de defaults
          break;
        case 'multi':
          //multiselect
          parsed.html = false; //gewoon niet
          parsed.filtertype = 'multi'; //ook apart
          break;
        case 'int':
        case 'float':
          parsed.html = false; //tenzij dat zo bij custom weer true wordt
          if (parsed.type === 'int') {
            parsed.sortval = function(rij: DatatableRijMeta) {
              return Number.parseInt(rij._val[this.key], 10) || 0;
            };
          } else if (parsed.type === 'float') {
            parsed.format = function(rij: DatatableRijMeta) {
              //altijd de scheiding goedzetten
              let decimaalveld = opties.decimaalscheider || ',';
              let strval = this.sortval(rij).toString().replace(/[,.]/, decimaalveld);
              if ('fixed' in this.typeArgs) {
                //vast aantal cijfers achter de komma
                let fixed = parseInt(this.typeArgs.fixed);
                if ((!isNaN(fixed)) && fixed >= 0) {
                  let geheel, decimalen;
                  [geheel, decimalen] = strval.split(decimaalveld, 2);
                  geheel = geheel || '0';
                  decimalen = decimalen || '';
                  let numdecimalen = decimalen ? decimalen.length : 0;
                  if (numdecimalen < fixed) {
                    decimalen += ''.padEnd(fixed - numdecimalen, '0');
                  } else if (numdecimalen > fixed) {
                    //hakken
                    decimalen = decimalen.substr(0, fixed);
                  }
                  strval = `${geheel}${decimaalveld}${decimalen}`;
                }
              }
              return strval;
            };
            parsed.sortval = function(rij: DatatableRijMeta) {
              return Number.parseFloat(rij._val[this.key]) || 0;
            };
          }

          //filteren
          if (parsed.typeArgs.filtertype === 'vantot') {
            parsed.filtertype = 'vantot';
            //filterval is dan numeriek, zie hierboven
          } else {
            parsed.filtertype = 'vrij';
            parsed.filterval = function(rij: DatatableRijMeta) {
              return String(rij._val[this.key]);
            };
          }
          break;
        case 'format':
          //eigen format op basis van een hele rij. Moet een format-functie hebben met alleen de rij als argument
          parsed._val = null; //niet nodig
          parsed.html = velddef.html;
          parsed.format = function(rij: DatatableRijMeta) {
            //html regelt de caching
            return (<rijfunctie<string>> velddef.format)(rij.data);
          };
          break;
        case 'actie':
          //actieveld
          parsed._val = null;
          parsed.html = true;
          parsed.filter = false;
          parsed.format = cb.noop;
          break;
        default:
          //de defaults
          parsed.html = velddef.html;
          break;
      }

      //custom format?
      if (velddef.format && (parsed.type !== 'format')) //bij format is hij al gezet
      {
        parsed.html = velddef.html; //dit overschrijft de setting per type dan weer
        parsed.format = function(rij: DatatableRijMeta) {
          //html mag erin
          return (<rijfunctie<string>> velddef.format)(rij._val[this.key], rij.data);
        };
      }

      //custom styles
      if (velddef.style) {
        parsed.style = function(rij: DatatableRijMeta) {
          return (<rijfunctie<string>> velddef.style)(rij._val[this.key], rij.data);
        };
      }

      //afwijkende sortval?
      if (velddef.sortval && typeof (velddef.sortval) === 'function') {
        parsed.sortval = function(rij: DatatableRijMeta) {
          return (<rijfunctie<string>> velddef.sortval)(rij._val[this.key], rij.data);
        };
      }

      //afwijkende filterval?
      if (velddef.filterval && typeof (velddef.filterval) === 'function') {
        parsed.filterval = function(rij: DatatableRijMeta) {
          return (<rijfunctie<string>> velddef.filterval)(rij._val[this.key], rij.data);
        };
      }


      //geen filter?
      if (!parsed.filter) {
        parsed.filtertype = 'hide';
      }

      //kopclasses
      parsed.kopclasses = [`datatable_${veld}`, `datatableveld_veldtype_${parsed.type}`];
      if (parsed.groep) {
        parsed.kopclasses.push('datatableveld_ingroep');
      }
      if (parsed.groep_eerste) {
        parsed.kopclasses.push('datatableveld_groep_eerste');
      }
      if (parsed.groep_laatste) {
        parsed.kopclasses.push('datatableveld_groep_laatste');
      }
      //filterclasses hebben eentje meer
      parsed.filterclasses = [`datatableveld_filtertype_${parsed.filtertype}`, ...parsed.kopclasses];

      //parsed is klaar
      t.velden.push(parsed);
      w.velden[veld] = parsed; //voor intern gebruik
    } //einde eerste for loop

    //en nog een keer, nu de groepen e.d. goed staat
    for (let parsed of t.velden) {
      //cellclasses
      let tdclasses = [`datatable_${parsed.key}`, `datatableveld_veldtype_${parsed.type}`];
      if (parsed.groep) {
        tdclasses.push('datatableveld_ingroep');
        if (parsed.groep_eerste) {
          tdclasses.push('datatableveld_groep_eerste');
        }
        if (parsed.groep_laatste) {
          tdclasses.push('datatableveld_groep_laatste');
        }
      }
      parsed.tdclasses = tdclasses.join(' ');
    }

    //groepen:
    if (!heeftgroepen) {
      t.groepen = null; //weg ermee
    }

    //selecteerbaar?
    if (d.selecteer) {
      t.selecteer = {
        label: d.selecteer.label || '',
        setProp: d.selecteer.setProp || false, //indata-property voor zetten selectie
        filterSelect: ('filterSelect' in d.selecteer) ? d.selecteer.filterSelect : true,
        check: d.selecteer.selecteerbaar || function() {
          return true;
        }
      };
    }

    //kiesbaar?
    if (d.kies) {
      //defaults
      //TODO: glyphicons weg, andere kiezen
      t.kies = Object.assign({
        label: '',
        positie: 'beide',
        content: {
          links: '<span class=\'glyphicon glyphicon-circle-arrow-right\'></span>',
          rechts: '<span class=\'glyphicon glyphicon-circle-arrow-left\'></span>'
        },
        class: 'btn btn-default',
        kiesbaar: () => true,
        kies: () => {
        }
      }, d.kies);

      t.kies.links = t.kies.positie === 'links' || t.kies.positie === 'beide';
      t.kies.rechts = t.kies.positie === 'rechts' || t.kies.positie === 'beide';
    }

    //we moeten de data opnieuw sorteren (op origvolgorde), opnieuw filteren en dan toepassen
    this.bepaalEnumdata(); //bepaal de mogelijke waarden op enumfilters
    this.sorteerData();
    this.filterData();
    this.toepassenData(true); //forceer reload

  }

  /**
   * Binnenkomende data is gewijzigd. We parsen en sorteren het.
   * we maken geen kopie, maar encapsuleren de live data in een object waarin ook metadata zit
   */
  private indataChanged() {
    this.log.debug(`Datatable ${this.w.lognaam}: indataChanged`);
    const w = this.w, t = this.t;
    const prevdata = w.data || []; //bewaar

    //snellen
    const rijclasses = this._def && this._def.rijclasses;
    const rijtitle = this._def && this._def.rijtitle;

    //en nieuw maken: stop elke datarij in een object met de data en veel snelle meta
    let i = 0;
    w.data = (this._data || []).map(rijdata => {
      //vind de oude data in prevdata: op object-identity of tracker-veld
      const oud = prevdata.find(rij =>
        rij.data === rijdata ||
        (t.tracker && rij.data[t.tracker] === rijdata[t.tracker]));

      let rij: DatatableRijMeta = {
        data: rijdata,
        infilter: true, //eerst, moeten we opnieuw bepalen
        //geselecteerd: bewaar de selectie, maar bij een nieuwe rij kan hij geselecteerd worden door de setProp-optie
        geselecteerd: oud ? oud.geselecteerd : t.selecteer && t.selecteer.setProp && rijdata[t.selecteer.setProp],
        origvolgorde: i++, //onthouden
        rijclasses: rijclasses ? rijclasses(rijdata) : [],
        rijtitle: rijtitle ? rijtitle(rijdata) : '',
        selecteerbaar: false, //wordt zo gezet
        kiesbaar: false, //wordt zo gezet
        stripe: false, //wordt later gezet
        //caches, zie getCached
        _val: <hash<any>> {}, //per veld
        style: <hash<any>> {},
        format: <hash<any>> {},
        format_html: <hash<any>> {},
        sortval: <hash<any>> {},
        filterval: <hash<any>> {},
        classes: <hash<any>> {}
      };
      for (let veldkey of Object.keys(w.velden)) {
        let veld = w.velden[veldkey];
        //we cachen zaken die gegarandeerd nodig zijn
        //die zijn er dus altijd
        SihwDatatable.getCached(veld, rij, '_val');
        SihwDatatable.getCached(veld, rij, 'format');
        SihwDatatable.getCached(veld, rij, 'style');
        SihwDatatable.getCached(veld, rij, 'classes');
        if (veld.html) {
          rij.format_html[veldkey] = this.sanitizer.bypassSecurityTrustHtml(rij.format[veldkey]);
        }
        rij.selecteerbaar = t.selecteer && t.selecteer.check(rij.data); //selecteerbaar?
        rij.kiesbaar = t.kies && t.kies.kiesbaar(rij.data); //kiesbaar?
      }
      return rij;
    });
    this.w.datalength = this.w.data.length;

    //en bijwerken:
    this.bepaalEnumdata();
    this.sorteerData();
    this.filterChanged(true); //altijd doen, omdat de data gewijzigd is
    this.toepassenData(false); //geen forceer
    this.log.debug(`markforcheck`);
    this.cdf.markForCheck();
  }

  /////////////// datahelpers


  /**
   * Bepaal de mogelijke waarden van velden met een enumfilter
   */
  private bepaalEnumdata() {
    this.log.debug(`Datatable: bepaalEnumdaa`);
    if (!this.t.toonfilter) {
      return; //klaar
    }
    for (let key of Object.keys(this.w.velden)) {
      let veld = this.w.velden[key];
      if (['enum', 'multi'].includes(veld.filtertype)) {
        veld.filteropties = [];
        let checkdubbel: hash<any> = {};
        for (let rij of this.w.data) {
          let val = veld.format(rij) || '';
          if (!checkdubbel[val.toLowerCase()]) {
            veld.filteropties.push(val);
            checkdubbel[val.toLowerCase()] = true;
          }
        }
        if (this.t.filter[veld.key]) {
          let checkvals = Array.isArray(this.t.filter[veld.key]) ? this.t.filter[veld.key] : [this.t.filter[veld.key]];
          for (let val of checkvals) {
            if (!checkdubbel[val.toLowerCase()]) {
              veld.filteropties.push(val);
              checkdubbel[val.toLowerCase()] = true;
            }
          }
        }
        //en dan nog de expliciet toegevoegde
        if (veld.typeArgs && veld.typeArgs.values && Array.isArray(veld.typeArgs.values)) {
          for (let val of veld.typeArgs.values) {
            if (!checkdubbel[val.toLowerCase()]) {
              veld.filteropties.push(val);
              checkdubbel[val.toLowerCase()] = true;
            }
          }
        }
        veld.filteropties.sort();
      }
    }
  }

  /**
   * Filter is gewijzigd. Hetgenereer de filterchain, opnieuw filteren, tonen en eventueel melden. Sortering wijzigt niet
   */
  private filterChanged(opVerzoek = false) {
    //bepaal hoe we filteren
    //t.filter bevat als keys de veldkeys, en als value te matchen waardes. Maar het type doet er wel toe
    //we loopen over de veldkeys in het filter en maken een functie die, gegeven de rij, true of false teruggeeft
    //deze gaan in de filterchain. De functies worden voor elke rij aangeroepen en moeten allemaal slagen om in het filter terecht te komen

    this.log.debug(`Datatable: filterChanged`);
    if (!this._def) {
      return;
    }

    if (this._def.filterOpVerzoek && (!opVerzoek)) {
      return; //pas als het ons gevraagd wordt
    }

    const w = this.w;
    const t = this.t;

    w.filterchain = []; //reset
    for (let veldkey of Object.keys(t.filter)) {
      let val = t.filter[veldkey];
      if (val === null || val === undefined) {
        continue; //niet nodig
      }
      const veld = w.velden[veldkey];
      let fn: ((rij: hash<any>) => boolean) | null = null; //als dit null blijft hoeft er voor dit filteritem geen functie worden uitgevoerd
      switch (veld.filtertype) {
        case 'vrij':
          if (val === '') {
            break; //geen test nodig
          }
          fn = rij => {
            //return true als er een substringmatch is
            let filterval = SihwDatatable.getCached(veld, rij, 'filterval');
            if (filterval === null) {
              filterval = '';
            }
            return String(filterval).includes(val.toLowerCase());
          };
          break;
        case 'enum':
          //bijna als vrij, alleen dan exacte match
          //testen op null gebeurt al hierboven buiten deze switch
          fn = rij => SihwDatatable.getCached(veld, rij, 'filterval') === val.toLowerCase();
          break;
        case 'multi':
          if (!(Array.isArray(val) && val.length)) {
            break; //geen test nodig
          }

          //filterval is al lowercase gemaakt?
          //exacte match in de geselecteerde waarden
          fn = rij => val.includes(SihwDatatable.getCached(veld, rij, 'filterval'));
          break;
        case 'bool':
          if (!val) {
            break; //niet te doen, geen functie nodig
          }
          fn = rij => (rij._val[veld.key] ? 1 : 2) === val;
          break;
        case 'datum':
          //via de datumpicker
          if (!(val && (val.van || val.tot))) {
            break; //niets te doen
          }
          fn = rij => {
            let fv = SihwDatatable.getCached(veld, rij, 'filterval');
            return fv && ((!val.van) || val.van <= fv) && ((!val.tot) || val.tot >= fv);
          };
          break;
        case 'vantot':
          //via decimaalveldpicker
          if ((!val) || (val.van === null && val.tot === null)) {
            break; //niets te doen
          }
          fn = rij => {
            //filterval is al numeriek
            let fv = SihwDatatable.getCached(veld, rij, 'filterval');
            return (fv !== null) && ((val.van === null) || val.van <= fv) && ((val.tot === null) || val.tot >= fv);
          };
          break;
        case 'hide':
          break; //doe niets
        default:
          fn = () => {
            this.log.warn(`Datatable ${w.lognaam}: missende filterfunctie voor filtertype`, veld.filtertype, veld.key, val);
            return true;
          };
          break;
      }
      if (fn) {
        w.filterchain.push(fn);
      }
    }
    //onfilterchanged?
    if (this._def.onFilterChanged) {
      //dan melden we het, maar gaan zelf ook door
      const vorige = w.vorigefilter || {};
      let gewijzigd: hash<any> = {}; //gewijzigde velden
      let wijzigingen = false;
      for (let veldkey of Object.keys(w.velden)) {
        let veld = w.velden[veldkey];
        let infilter = (veldkey in t.filter) && (t.filter[veldkey] !== null);
        let inprev = (veldkey in vorige) && (vorige[veldkey] !== null);
        if (infilter !== inprev ||
          (
            infilter && !isEqual(t.filter[veldkey], vorige[veldkey])
          )
        ) {
          //deze geldt als gewijzigd: bweaar nieuw, oud
          gewijzigd[veldkey] = [cloneDeep(t.filter[veldkey]), cloneDeep(vorige[veldkey])];
          wijzigingen = true;
        }
      }
      if (wijzigingen) {
        //we moeten aanroepen
        try {
          void this._def.onFilterChanged(gewijzigd);
        } catch (e) {
          this.log.warn(e);
        }
      }
    }
    //vorigefilter kan nu bijgewerkt worden
    w.vorigefilter = cloneDeep(t.filter);

    //maar hoe dan ook bijwerken, zat ziet er beter uit dan wachten op nieuwe data
    //even naar boven scrollen
    if (this.tableContainer) {
      this.tableContainer.nativeElement.scrollTo(0, 0);
    }
    this.filterData();
    this.toepassenData(); //opnieuw toepassen, want dit kan tijdens de oorspronkelijke vulloop gebeuren
  }

  /**
   * Sorteer de data opnieuw. Alleen intern, daarna moet toepassenData nog.
   */
  private sorteerData() {
    this.log.debug(`Datatable: sorteerData`);
    const w = this.w, t = this.t, d = this._def;
    let sorteerop: hash<any> | string | undefined, sortering: number;
    let opprop = false;

    if (w.sorteerveld) {
      sorteerop = w.sorteerveld;
      sortering = w.sorteerveld.sortering || 1;
    } else if (d && d.standaardsortering) {
      if (d.standaardsortering.keytype === 'prop') {
        //sorteren op een prop in de data, mag alleen op hoofdniveau
        sorteerop = d.standaardsortering.key;
        opprop = true; //we werken met een property, niet met een heel veld
      } else {
        //op veld: default keytype
        sorteerop = w.velden[d.standaardsortering.key];
      }
      sortering = (d.standaardsortering.richting || 1) < 0 ? -1 : 1;
    }

    if (!sorteerop) //nog niets bedacht
    {
      //dan met de origvolgorde
      w.data.sort((a, b) => (a.origvolgorde || 0) > (b.origvolgorde || 0) ? 1 : -1);
      return;
    }


    if (opprop) {
      //sorteer op hoofproperty
      w.data.sort((a, b) => {
        let va = (a.data[<string> sorteerop] || 0);
        let vb = (b.data[<string> sorteerop] || 0);
        if (va > vb) {
          return sortering;
        } else if (va < vb) {
          return sortering * -1;
        } else {
          //origvolgorde
          return sortering * ((a.origvolgorde || 0) > (b.origvolgorde || 0) ? 1 : -1);
        }
      });
    } else {
      //vergelijk de sorteerwaarden volgens dit veld
      //en wijzig de richting als de sortering van het veld -1 is
      w.data.sort((a, b) => {
        let va = SihwDatatable.getCached(<hash<any>> sorteerop, a, 'sortval');
        let vb = SihwDatatable.getCached(<hash<any>> sorteerop, b, 'sortval');
        if (va === null) {
          va = '';
        }
        if (vb === null) {
          vb = '';
        }
        if (va > vb) {
          return sortering;
        } else if (va < vb) {
          return sortering * -1;
        } else {
          //origvolgorde
          return sortering * ((a.origvolgorde || 0) > (b.origvolgorde || 0) ? 1 : -1);
        }
      });
    }
  }

  /**
   * Kopieer onze interne, gesorteerde data naar de templatedata. Doe dat in stripes als het veel is
   * @param forceer
   */
  private toepassenData(forceer: boolean = false) {
    this.log.debug(`Datatable: toepassenData`);
    const w = this.w, t = this.t, d = this._def, $this = this;

    w.datageneratie++; //bijhouden in de async
    if (!w.data) {
      w.data = [];
      w.datalength = 0;
    }

    //als we klaar zijn met laden en er komt nieuwe data, dan kunnen we snel handelen
    //heuristiek: moet ongeveer net zoveel zijn
    //ngFor  werkt dan snel met zijn hashkey
    if ((!forceer) && (!w.loading) && t.data && t.data.length && Math.abs(t.data.length - w.data.length) < 10) {
      t.data = w.data; //hopla
      this.cdf.markForCheck(); //bijwerken
      return;
    }

    w.loading = true; //we zijn nu aan het laden, gebruikt in logica rond filteren hieronder
    //okee: druppelen met batches, voor de snelheid eerst alles dat uit het filter valt skippen

    let onzegeneratie = w.datageneratie; //local closure
    t.data = []; //deze gaan we vullen
    let vuldata = w.data.filter(rij => rij.infilter);
    const batch = 20; //hoeveel rijen per batch

    //start de vuller: geef door of er gefilterd is
    naartemplate(0, 15, vuldata.length != w.datalength);

    //closure functie
    function naartemplate(index: number, dezebatch: number, gefilterd: boolean) {
      if (onzegeneratie != w.datageneratie) {
        //er is al een nieuwe vuller begonnen. Wij stoppen
        return;
      }
      //we vullen een stukje
      let laatste = index + dezebatch;
      t.data.push(...vuldata.slice(index, laatste));
      $this.cdf.markForCheck();
      //nog meer?
      if (laatste < vuldata.length) {
        //ga door, in de volgende batch
        setTimeout(() => {
          naartemplate(laatste, batch, gefilterd);
        }, 20);
      } else {
        //klaar met vullen. Als dit alleen het gefilterde deel was, moet de rest nog
        //als het goed is pakt ngFor dat goed op door te herkennen wat er al staat

        if (gefilterd) {
          vuldata = w.data.filter(rij => !rij.infilter);
          setTimeout(() => {
            naartemplate(0, batch, false);
          }, 20);
        } else {
          //dus nu helemaal klaar. Sorteren en opnieuw vullen
          //in juiste volgorde. Omdat alle dom er is gaat dat snel
          t.data = w.data;
          w.loading = false;
        }
        //TODO: checkcontainerscroll?
        $this.keyedTimeout.set('stripe', () => {
          $this.stripe();
        }, 20);
      }
    }
  }

  /**
   * Pas het huidige filter toe op de data.
   */
  private filterData() {
    this.log.debug(`Datatable: filterData`);
    for (let rij of this.w.data) {
      //bepaal of deze in filter zit
      rij.infilter = this.w.filterchain.every(filterfn => filterfn(rij));
    }
    //tellen
    this.t.numfilter = this.w.data.filter(r => r.infilter).length;
    this.bepaalSelectie();
    this.keyedTimeout.set('stripe', () => {
      this.stripe();
    }, 20);
  }

  /**
   * Bepaal of alles geselecteerd is of niet
   * dit gaat alleen om wat er zichtbaar is
   */
  private bepaalSelectie() {
    this.log.debug(`Datatable: bepaalSelectie`);
    if (!(this.t.selecteer && this.w.data && this.w.data.length)) {
      this.t.allesGeselecteerd = false;
    } else {
      this.t.allesGeselecteerd = !(this.w.data.some(rij => rij.infilter && rij.selecteerbaar && (!rij.geselecteerd)));
    }
    this.t.numgeselecteerd = this.w.data.filter(rij => rij.infilter && rij.geselecteerd).length;
    this.cdf.markForCheck();
  }


  ///////////////////////// HELPERS //////////////////////////////

  /**
   * Run over de zichtbare rijen om te stripen
   */
  private stripe() {
    this.log.debug(`Datatable: stripe`);
    let stripe = false;
    for (let rij of this.t.data) //run over de zichtbare gesorteerde data
    {
      if (rij.infilter) {
        rij.stripe = stripe; //boolean flag
        stripe = !stripe;
      }
    }
  }

  /**
   * Return een object met standaardcallbacks
   * @private
   */
  private _standaardCallbacks() {
    //in de callbacks is de this gelijk aan het veldobject in t.velden
    //en $this zijn wij
    const $this = this;

    return {
      standaardformat: function(this: hash<any>, rij: DatatableRijMeta): any {
        if (this.html) {
          $this.log.debug(`Aanroepen standaardformat-functie met html-content. Gaat dat goed?`);
        }
        return rij._val[this.key]; //teruggeven
        // return this.html ? $sce.trustAsHtml(rij._val[this.key]) : rij._val[this.key];
      },

      standaardstyle: function(rij: DatatableRijMeta) {
        return ''; //niets
      },

      standaardsortval: function(this: hash<any>, rij: DatatableRijMeta): string | null {
        let formatted = rij.format[this.key]; //kan sanitized zijn
        if (formatted === null) {
          return formatted;
        }
        if (this.html) {
          //TODO: maak er tekst van. Dat deden we via jquery, kan het anders?
          $this.log.debug(`standaard sortval bij html - gaat dat goed?`);
          /*  try {
              formatted = formatted.valueOf(); //als er een value-of is, dan nemen we die
            }
            catch (_e) {
            }
            if (formatted) {
              //haal html eraf
              formatted = $('<div/>').html(formatted).text();
            }*/
        }
        formatted = String(formatted).toLowerCase();
        return formatted;
      },

      //standaard hetzelfde als sortval
      standaardfilterval: function(rij: DatatableRijMeta) {
        return SihwDatatable.getCached(this, rij, 'sortval');
      },

      //niks
      noop: function() {
      }
    };
  }

////////////////// STATIC HELPERS /////////////////////////////
  /**
   * Haal een formatted waarde op van een veld in deze rij, cached of via functie. Public want via de callbacks. Static want geen gedoe met this in callbacks. Maar er is ook een non-static versie voor in het template
   * @param veld
   * @param rij
   * @param proces is '_val', 'format', 'style', 'classes', 'sortval' of 'filterval'
   */
  public static getCached(veld: hash<any>, rij: hash<any>, proces: '_val' | 'format' | 'style' | 'classes' | 'sortval' | 'filterval'): any {
    if (veld.key in rij[proces]) {
      return rij[proces][veld.key]; //cached
    } else {
      return (rij[proces][veld.key] = (veld[proces] && veld[proces](rij)));
    }
  }

  //en voor het template
  public getCached(veld: hash<any>, rij: hash<any>, proces: '_val' | 'format' | 'style' | 'classes' | 'sortval' | 'filterval'): any {
    return SihwDatatable.getCached(veld, rij, proces);
  }
}
